// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { EMPTY, filter, mergeMap } from 'rxjs';
import { isActionOf } from 'typesafe-actions';
import type { Epic } from 'redux-observable';
import type {
  Action,
  DirectCredentialsActionData,
  EpicDependencies,
  RootState,
} from '../types';
import { submitDirectCredentialsAction } from '../actions';

// The agent reads '.' as the local machine.
const LOCAL_DOMAIN = '.';
const DOMAIN_SEPARATOR = '\\';
const UPN_SEPARATOR = '@';

// UPN (user@domain) goes as typed; DOMAIN\user (AD) and .\user (local) are split;
// a bare username is a local login.
export const toAgentCredentials = ({
  username,
  password,
}: DirectCredentialsActionData): CredentialData => {
  const typed = username.trim();
  const separatorIndex = typed.indexOf(DOMAIN_SEPARATOR);

  if (separatorIndex >= 0) {
    return {
      username: typed.slice(separatorIndex + 1),
      password,
      domain: typed.slice(0, separatorIndex) || LOCAL_DOMAIN,
    };
  }
  if (typed.includes(UPN_SEPARATOR)) {
    return { username: typed, password };
  }
  return { username: typed, password, domain: LOCAL_DOMAIN };
};

// The reducer closes the prompt on this action; the agent owns the login result.
export const submitDirectCredentialsEpic: Epic<
  Action,
  Action,
  RootState,
  Pick<EpicDependencies, 'sendAuthnDataToAgent'>
> = (action$, _state$, { sendAuthnDataToAgent }) =>
  action$.pipe(
    filter(isActionOf(submitDirectCredentialsAction)),
    mergeMap(({ payload }) => {
      sendAuthnDataToAgent(toAgentCredentials(payload));
      return EMPTY;
    }),
  );
